import React, { useState } from "react";
import ImageUploading from "react-images-uploading";
import { useFormContext } from "@/providers";
import { ModalImage, ImageCard } from "./ImageComponents";
// import { handleConvert } from "@/helpers/imageconverter";
import { useLocation } from "react-router-dom";
import { errorPopup } from "@/helpers";

export const ImagesForm = () => {
  const location = useLocation();
  const { listId = "" } = location.state || {};
  const maxNumber = 70;

  const { setValue, watch, errors, fieldRefs } = useFormContext();
  const images = watch("images") || []; // Watching the images field from the form context
  const imgdescription = watch("imgdescription") || []; // Watching the images field from the form context
  const deletedImages = watch("deleted_images") || [];
  const [featuredIndex, setFeaturedIndex] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [description, setDescription] = useState("");

  // Update images in the form context
  const onChange = (imageList) => {
    setValue("images", imageList);
  };

  const moveImage = (fromIndex, toIndex) => {
    const updatedImages = [...images];
    const [movedImage] = updatedImages.splice(fromIndex, 1);
    updatedImages.splice(toIndex, 0, movedImage);
    setValue("images", updatedImages);
    setFeaturedIndex(toIndex);

    // Safely move the description
    if (imgdescription && Array.isArray(imgdescription)) {
      const updatedDescriptions = [...imgdescription];

      // Ensure we have a description to move (might be undefined)
      const [movedDescription] =
        updatedDescriptions.length > fromIndex
          ? updatedDescriptions.splice(fromIndex, 1)
          : [undefined];

      if (movedDescription !== undefined) {
        updatedDescriptions.splice(toIndex, 0, movedDescription);
      } else if (toIndex <= updatedDescriptions.length) {
        // If no description existed, maintain array length consistency
        updatedDescriptions.splice(toIndex, 0, "");
      }

      setValue("imgdescription", updatedDescriptions);
    }
  };

  const handleDelete = (index, id) => {
    if (listId && id) {
      setValue("deleted_images", [...deletedImages, id]);
    }
    const updatedImages = images.filter((_, i) => i !== index);
    setValue("images", updatedImages);
  };

  const handleStarClick = (index) => {
    if (index === 0) return;
    moveImage(index, 0);
  };

  const handleDescriptionClick = (image, index) => {
    setSelectedImage({ ...image, index });
    setDescription(image.description || "");
    setIsModalOpen(true);
    // window.scrollTo({ top: 1500, behavior: "smooth" });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
  };

  const saveDescription = () => {
    if (selectedImage) {
      const updatedImages = [...images];
      updatedImages[selectedImage.index] = {
        ...selectedImage,
        description: description,
      };
      setValue("images", updatedImages);

      // Safely update descriptions
      if (!Array.isArray(imgdescription)) {
        // Initialize array if it doesn't exist or is invalid
        setValue("imgdescription", []);
        return;
      }

      const updatedDescriptions = [...imgdescription];

      // Ensure array is long enough (fill with empty strings if needed)
      while (updatedDescriptions.length <= selectedImage.index) {
        updatedDescriptions.push("");
      }

      // Update the description
      updatedDescriptions[selectedImage.index] = description;

      // Validate the update didn't corrupt the array
      if (updatedDescriptions.length > imgdescription.length + 1) {
        throw new Error("Array length mismatch during update");
      }

      setValue("imgdescription", updatedDescriptions);
    }
    closeModal();
  };

  return (
    <>
      <div
        className="p-4 bg-white col-md-12"
        ref={(el) => (fieldRefs.current["images"] = el)}
      >
        <h2 className="mb-4 text-2xl font-bold text-center">Media</h2>
        <ImageUploading
          multiple
          value={images}
          onChange={onChange}
          maxNumber={maxNumber}
          dataURLKey="data_url"
          acceptType={["jpg", "jpeg", "png", "webp"]}
          beforeUpload={(files) => {
            // Additional validation before upload
            const validTypes = [
              "image/jpeg",
              "image/jpg",
              "image/png",
              "image/webp",
            ];
            const invalidFiles = files.filter(
              (file) => !validTypes.includes(file.type)
            );

            if (invalidFiles.length > 0) {
              errorPopup("", `Only JPG, PNG, and WEBP images are allowed.`);
              return false; // Prevent upload
            }

            // Optional: Check file size (e.g., 5MB max)
            const maxSize = 5 * 1024 * 1024; // 5MB
            const tooLargeFiles = files.filter((file) => file.size > maxSize);

            if (tooLargeFiles.length > 0) {
              errorPopup("", `Files must be smaller than 5MB`);
              return false;
            }

            return true; // Proceed with upload
          }}
        >
          {({ imageList, onImageUpload, isDragging, dragProps }) => (
            <div className="upload__image-wrapper">
              <div
                className="fu-text"
                style={isDragging ? { color: "red" } : null}
                onClick={onImageUpload}
                {...dragProps}
              >
                <div className="add-list-media-wrap">
                  <div className="listsearch-input-item fl-wrap">
                    <div className="fuzone">
                      <div className="fu-text">
                        <span>
                          <i className="fal fa-images" /> PLEASE BE PATIENT
                          WHILE YOUR PHOTOS UPLOAD :)
                          <br />
                          Drag and drop the images to customize the gallery
                          order.
                          <br />
                          Click on the star icon to set the featured image.
                          <br />
                          <span>(Minimum size 1440 x 900 px)</span>
                        </span>
                        <div className="photoUpload-files fl-wrap" />
                        <div className="!text-center pt-[27px]">
                          <span className="custom_image_button">
                            Select and upload
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="photoUpload-files fl-wrap" />
              </div>

              <div
                className="row"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "flex-start",
                }}
              >
                {imageList.map((image, index) => (
                  <ImageCard
                    key={index}
                    image={image}
                    index={index}
                    moveImage={moveImage}
                    onDelete={handleDelete}
                    isFeatured={index === featuredIndex}
                    onStarClick={handleStarClick}
                    description={description}
                    onDescriptionClick={handleDescriptionClick}
                  />
                ))}
              </div>
            </div>
          )}
        </ImageUploading>

        {isModalOpen && (
          <ModalImage
            saveDescription={saveDescription}
            closeModal={closeModal}
            selectedImage={selectedImage}
            description={description}
            isModalOpen={isModalOpen}
            setDescription={setDescription}
          />
        )}

        {errors.images && (
          <p className="mt-1 text-red-500">{errors.images.message}</p>
        )}
      </div>
    </>
  );
};
